import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Modal from '../components/common/Modal';
import BulkUpload from '../components/common/BulkUpload';
import { useAuth } from '../context/AuthContext';
import { localDateStr } from '../utils/dateUtils';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const availColor = (avail, total) => {
  if (!total) return '#64748b';
  const pct = avail / total;
  if (pct > 0.5) return '#059669';
  if (pct > 0.2) return '#d97706';
  return '#dc2626';
};

const emptyInventoryForm = () => ({ product_name: '', batch_number: '', total_quantity: '', unit: 'units', notes: '' });
const emptyIssueForm = () => ({ sample_id: '', customer_id: '', client_id: '', quantity: '', issue_date: localDateStr(), purpose: '', notes: '' });

function AckButton({ issuanceId, onSuccess }) {
  const [saving, setSaving] = React.useState(false);
  const handleAck = async () => {
    setSaving(true);
    try {
      await api.put(`/samples/issuances/${issuanceId}/acknowledge`);
      onSuccess();
    } catch (_) {}
    setSaving(false);
  };
  return (
    <button className="btn btn-sm btn-secondary" onClick={handleAck} disabled={saving} style={{ fontSize: '0.72rem' }}>
      {saving ? '…' : 'Mark Received'}
    </button>
  );
}

export default function SampleManagement() {
  const { isManager } = useAuth();

  const [tab, setTab] = useState('inventory');
  const [inventory, setInventory] = useState([]);
  const [issuances, setIssuances] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [clients, setClients] = useState([]);
  const [addStockModal, setAddStockModal] = useState({ open: false, form: emptyInventoryForm(), saving: false, error: '' });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [issueModal, setIssueModal] = useState({ open: false, sample: null, form: emptyIssueForm(), saving: false, error: '' });

  const loadInventory = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/samples/inventory');
      setInventory(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadIssuances = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/samples/issuances');
      setIssuances(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInventory();
    api.get('/customers', { params: { limit: 500 } }).then(r => setCustomers(r.data.data || r.data || [])).catch(() => {});
    api.get('/clients', { params: { limit: 500, approval_status: 'approved' } }).then(r => setClients(r.data.data || r.data || [])).catch(() => {});
  }, [loadInventory]);

  useEffect(() => {
    if (tab === 'issuances') loadIssuances();
  }, [tab, loadIssuances]);

  const handleAddStock = async () => {
    const { form } = addStockModal;
    if (!form.product_name || !form.total_quantity) {
      setAddStockModal(m => ({ ...m, error: 'Product name and quantity are required.' }));
      return;
    }
    setAddStockModal(m => ({ ...m, saving: true, error: '' }));
    try {
      await api.post('/samples/inventory', { ...form, total_quantity: Number(form.total_quantity) });
      setAddStockModal({ open: false, form: emptyInventoryForm(), saving: false, error: '' });
      loadInventory();
    } catch (err) {
      setAddStockModal(m => ({ ...m, saving: false, error: err.response?.data?.message || 'Failed to add stock.' }));
    }
  };

  const handleIssue = async () => {
    const { form, sample } = issueModal;
    if (!form.quantity || !form.issue_date) {
      setIssueModal(m => ({ ...m, error: 'Quantity and issue date are required.' }));
      return;
    }
    setIssueModal(m => ({ ...m, saving: true, error: '' }));
    try {
      await api.post('/samples/issue', {
        sample_id: sample.id,
        customer_id: form.customer_id || undefined,
        client_id: form.client_id || undefined,
        quantity: Number(form.quantity),
        issue_date: form.issue_date,
        purpose: form.purpose || undefined,
        notes: form.notes || undefined,
      });
      setIssueModal({ open: false, sample: null, form: emptyIssueForm(), saving: false, error: '' });
      loadInventory();
      if (tab === 'issuances') loadIssuances();
    } catch (err) {
      setIssueModal(m => ({ ...m, saving: false, error: err.response?.data?.message || 'Failed to issue sample.' }));
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Sample Management</h1>
          <p className="page-subtitle">Track sample inventory and issuances to customers</p>
        </div>
        {isManager && tab === 'inventory' && (
          <>
            <button className="btn btn-secondary" onClick={() => setBulkOpen(true)}>
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              Import
            </button>
            <button className="btn btn-primary" onClick={() => setAddStockModal({ open: true, form: emptyInventoryForm(), saving: false, error: '' })}>
              + Add Stock
            </button>
          </>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '2px solid var(--border)' }}>
        {[{ key: 'inventory', label: 'Inventory' }, { key: 'issuances', label: 'Issuances' }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              padding: '10px 20px', border: 'none', background: 'transparent', cursor: 'pointer',
              fontWeight: tab === t.key ? 700 : 500, fontSize: '0.875rem',
              color: tab === t.key ? 'var(--primary)' : 'var(--text-secondary)',
              borderBottom: tab === t.key ? '2px solid var(--primary)' : '2px solid transparent',
              marginBottom: -2,
            }}>{t.label}</button>
        ))}
      </div>

      {/* Inventory Tab */}
      {tab === 'inventory' && (
        <div className="card">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th>Batch #</th>
                  <th>Total Qty</th>
                  <th>Distributed</th>
                  <th>Available</th>
                  <th>Unit</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={8}><div className="loading-overlay"><div className="spinner" /></div></td></tr>}
                {!loading && inventory.length === 0 && (
                  <tr><td colSpan={8}><div className="empty-state"><p>No samples in inventory. Add stock to get started.</p></div></td></tr>
                )}
                {!loading && inventory.map(item => {
                  const avail = item.total_quantity - item.distributed_quantity;
                  const outOfStock = avail <= 0;
                  return (
                    <tr key={item.id}>
                      <td style={{ fontWeight: 600 }}>{item.product_name}</td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: '0.83rem' }}>{item.batch_number || '—'}</td>
                      <td>{item.total_quantity}</td>
                      <td>{item.distributed_quantity}</td>
                      <td>
                        {outOfStock ? (
                          <span style={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>Out of Stock</span>
                        ) : (
                          <span style={{ fontWeight: 700, color: availColor(avail, item.total_quantity) }}>
                            {avail}
                          </span>
                        )}
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.83rem' }}>{item.unit}</td>
                      <td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                        {item.notes || '—'}
                      </td>
                      <td>
                        <button className="btn btn-sm btn-secondary" disabled={outOfStock}
                          onClick={() => !outOfStock && setIssueModal({ open: true, sample: item, form: { ...emptyIssueForm(), sample_id: item.id }, saving: false, error: '' })}
                        >
                          Issue
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Issuances Tab */}
      {tab === 'issuances' && (
        <div className="card">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Product</th>
                  <th>Batch</th>
                  <th>Customer / Client</th>
                  <th>Qty</th>
                  <th>Purpose</th>
                  <th>Issued By</th>
                  <th>Acknowledgement</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={8}><div className="loading-overlay"><div className="spinner" /></div></td></tr>}
                {!loading && issuances.length === 0 && (
                  <tr><td colSpan={8}><div className="empty-state"><p>No issuances recorded yet.</p></div></td></tr>
                )}
                {!loading && issuances.map(iss => (
                  <tr key={iss.id}>
                    <td style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{fmtDate(iss.issue_date)}</td>
                    <td style={{ fontWeight: 600 }}>{iss.product_name}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: '0.83rem' }}>{iss.batch_number || '—'}</td>
                    <td>{iss.customer_name || iss.client_name || '—'}</td>
                    <td style={{ fontWeight: 600 }}>{iss.quantity} {iss.unit}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: '0.83rem' }}>{iss.purpose || '—'}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: '0.83rem' }}>{iss.issued_by_name || '—'}</td>
                    <td>
                      {iss.acknowledgement ? (
                        <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: 12, background: '#dcfce7', color: '#15803d', fontWeight: 600 }}>✓ Received</span>
                      ) : (
                        <AckButton issuanceId={iss.id} onSuccess={loadIssuances} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Stock Modal */}
      <Modal
        open={addStockModal.open}
        onClose={() => !addStockModal.saving && setAddStockModal({ open: false, form: emptyInventoryForm(), saving: false, error: '' })}
        title="Add Sample Stock"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setAddStockModal({ open: false, form: emptyInventoryForm(), saving: false, error: '' })} disabled={addStockModal.saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleAddStock} disabled={addStockModal.saving}>
            {addStockModal.saving ? 'Saving…' : 'Add Stock'}
          </button>
        </>}
      >
        {addStockModal.error && (
          <div style={{ background: 'var(--error-bg, #fef2f2)', color: 'var(--error, #dc2626)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: '0.875rem' }}>
            {addStockModal.error}
          </div>
        )}
        <div style={{ display: 'grid', gap: 14 }}>
          <div>
            <label className="form-label">Product Name <span style={{ color: 'var(--error)' }}>*</span></label>
            <input className="form-control" placeholder="e.g. Paracetamol 500mg"
              value={addStockModal.form.product_name}
              onChange={e => setAddStockModal(m => ({ ...m, form: { ...m.form, product_name: e.target.value } }))} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Batch Number</label>
              <input className="form-control" placeholder="e.g. BT2025-001"
                value={addStockModal.form.batch_number}
                onChange={e => setAddStockModal(m => ({ ...m, form: { ...m.form, batch_number: e.target.value } }))} />
            </div>
            <div>
              <label className="form-label">Unit</label>
              <input className="form-control" placeholder="units, vials, tablets…"
                value={addStockModal.form.unit}
                onChange={e => setAddStockModal(m => ({ ...m, form: { ...m.form, unit: e.target.value } }))} />
            </div>
          </div>
          <div>
            <label className="form-label">Total Quantity <span style={{ color: 'var(--error)' }}>*</span></label>
            <input className="form-control" type="number" min="1" placeholder="0"
              value={addStockModal.form.total_quantity}
              onChange={e => setAddStockModal(m => ({ ...m, form: { ...m.form, total_quantity: e.target.value } }))} />
          </div>
          <div>
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={2} placeholder="Optional notes…"
              value={addStockModal.form.notes}
              onChange={e => setAddStockModal(m => ({ ...m, form: { ...m.form, notes: e.target.value } }))} />
          </div>
        </div>
      </Modal>

      {/* Issue Sample Modal */}
      <Modal
        open={issueModal.open}
        onClose={() => !issueModal.saving && setIssueModal({ open: false, sample: null, form: emptyIssueForm(), saving: false, error: '' })}
        title={`Issue Sample: ${issueModal.sample?.product_name || ''}`}
        footer={<>
          <button className="btn btn-secondary" onClick={() => setIssueModal({ open: false, sample: null, form: emptyIssueForm(), saving: false, error: '' })} disabled={issueModal.saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleIssue} disabled={issueModal.saving}>
            {issueModal.saving ? 'Issuing…' : 'Issue Sample'}
          </button>
        </>}
      >
        {issueModal.sample && (
          <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
            Available: <strong style={{ color: availColor(issueModal.sample.total_quantity - issueModal.sample.distributed_quantity, issueModal.sample.total_quantity) }}>
              {issueModal.sample.total_quantity - issueModal.sample.distributed_quantity} {issueModal.sample.unit}
            </strong>
          </div>
        )}
        {issueModal.error && (
          <div style={{ background: 'var(--error-bg, #fef2f2)', color: 'var(--error, #dc2626)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: '0.875rem' }}>
            {issueModal.error}
          </div>
        )}
        <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Quantity <span style={{ color: 'var(--error)' }}>*</span></label>
              <input className="form-control" type="number" min="1" placeholder="0"
                value={issueModal.form.quantity}
                onChange={e => setIssueModal(m => ({ ...m, form: { ...m.form, quantity: e.target.value } }))} />
            </div>
            <div>
              <label className="form-label">Issue Date <span style={{ color: 'var(--error)' }}>*</span></label>
              <input className="form-control" type="date"
                value={issueModal.form.issue_date}
                onChange={e => setIssueModal(m => ({ ...m, form: { ...m.form, issue_date: e.target.value } }))} />
            </div>
          </div>
          <div>
            <label className="form-label">Customer</label>
            <select className="form-control form-select"
              value={issueModal.form.customer_id}
              onChange={e => setIssueModal(m => ({ ...m, form: { ...m.form, customer_id: e.target.value } }))}>
              <option value="">Select customer (optional)</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Client</label>
            <select className="form-control form-select"
              value={issueModal.form.client_id}
              onChange={e => setIssueModal(m => ({ ...m, form: { ...m.form, client_id: e.target.value } }))}>
              <option value="">Select client (optional)</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Purpose</label>
            <input className="form-control" placeholder="e.g. Clinical trial, Demo…"
              value={issueModal.form.purpose}
              onChange={e => setIssueModal(m => ({ ...m, form: { ...m.form, purpose: e.target.value } }))} />
          </div>
          <div>
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={2} placeholder="Optional notes…"
              value={issueModal.form.notes}
              onChange={e => setIssueModal(m => ({ ...m, form: { ...m.form, notes: e.target.value } }))} />
          </div>
        </div>
      </Modal>

      <BulkUpload
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title="Import Sample Inventory"
        uploadUrl="/samples/inventory/bulk"
        columns={[
          { key: 'product_name', label: 'Product Name', required: true },
          { key: 'batch_number', label: 'Batch Number' },
          { key: 'total_quantity', label: 'Total Quantity', required: true },
          { key: 'unit', label: 'Unit' },
          { key: 'notes', label: 'Notes' },
        ]}
        onSuccess={loadInventory}
      />
    </div>
  );
}
